import { useState, useCallback, useMemo } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import { FormattedMessage } from '@zenbusiness/application-commons-intl';
import Box from '@zenbusiness/zui/Box';
import Button from '@zenbusiness/zui/Button';
import ErrorState from '@zenbusiness/zui/ErrorState';
import LoadingSpinner from '@zenbusiness/zui/LoadingSpinner';
import PageContainer from '@zenbusiness/zui/PageContainer';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';

import { ChevronLeftRoundedIcon, CloseIcon, getSubscriptionRestartPaths } from './constants';
import { getRestartMessages } from './messages';
import RestartConfirmStep from './RestartConfirmStep';
import RestartLoadingStep from './RestartLoadingStep';
import RestartResultStep from './RestartResultStep';
import { useSubscriptionRestart } from '../../hooks/useSubscriptionRestart';

export default function SubscriptionRestartPage() {
  const { businessEntityUuid, orderBillingItemUuid } = useParams();
  const navigate = useNavigate();

  const [restartPhase, setRestartPhase] = useState('confirm');
  const [restartSuccess, setRestartSuccess] = useState(null);

  const {
    subscription,
    paymentMethod,
    loading,
    hasError,
    submitChange
  } = useSubscriptionRestart();

  const paths = useMemo(
    () => getSubscriptionRestartPaths(businessEntityUuid),
    [businessEntityUuid]
  );

  const trackRenewSubmit = useButtonPressedCallback({
    elementName: 'restart_subscription_submit',
    page: 'subscription_restart'
  });

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleClose = useCallback(() => {
    navigate(paths.subscriptions);
  }, [navigate, paths.subscriptions]);

  const handleSubmit = useCallback(async (event) => {
    trackRenewSubmit(event);
    setRestartPhase('loading');
    try {
      const result = await submitChange({
        variables: {
          input: {
            orderBillingItemUuid,
            changeType: 'active'
          }
        }
      });
      const statusCode = result?.data?.billingSubscriptionChange?.statusCode;
      setRestartSuccess(Boolean(statusCode && statusCode >= 200 && statusCode < 300));
    } catch {
      setRestartSuccess(false);
    } finally {
      setRestartPhase('result');
    }
  }, [submitChange, orderBillingItemUuid, trackRenewSubmit]);

  const handleReturnDashboard = useCallback(() => {
    navigate(paths.dashboard);
  }, [navigate, paths.dashboard]);

  const handleReturnSubscriptions = useCallback(() => {
    navigate(paths.subscriptions);
  }, [navigate, paths.subscriptions]);

  const handleContactUs = useCallback(() => {
    navigate(paths.help);
  }, [navigate, paths.help]);

  if (loading) {
    return (
      <PageContainer>
        <Stack alignItems="center" justifyContent="center" p={6}>
          <LoadingSpinner />
        </Stack>
      </PageContainer>
    );
  }

  if (hasError || !subscription) {
    return (
      <PageContainer>
        <ErrorState
          title={<FormattedMessage id="dashboard.subscriptionRestart.error.loading.title" />}
          description={<FormattedMessage id="dashboard.subscriptionRestart.error.loading.message" />}
        />
      </PageContainer>
    );
  }

  return (
    <Box bgcolor="background.default" minHeight="100vh">
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        py={1.5}
        px={2}
        bgcolor="background.default"
      >
        <Button
          variant="text"
          onClick={handleBack}
          startIcon={<ChevronLeftRoundedIcon />}
        >
          <FormattedMessage id="dashboard.subscriptionRestart.header.back" />
        </Button>
        <Text size="xl" weight="bold">
          <FormattedMessage id={getRestartMessages(subscription.inTrial).pageTitle} />
        </Text>
        <Button variant="text" onClick={handleClose} endIcon={<CloseIcon fontSize="small" />}>
          <FormattedMessage id="dashboard.subscriptionRestart.header.close" />
        </Button>
      </Stack>

      <Stack gap={3} maxWidth={600} mx="auto" py={5} px={3}>
        {restartPhase === 'loading' && <RestartLoadingStep />}
        {restartPhase === 'result' && (
          <RestartResultStep
            success={restartSuccess}
            onReturnDashboard={handleReturnDashboard}
            onReturnSubscriptions={handleReturnSubscriptions}
            onContactUs={handleContactUs}
          />
        )}
        {restartPhase === 'confirm' && (
          <RestartConfirmStep
            subscription={subscription}
            paymentMethod={paymentMethod}
            onSubmit={handleSubmit}
            submitting={false}
          />
        )}
      </Stack>
    </Box>
  );
}
